import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const REMIND = [["", "No reminder"], [5, "5 min before"], [10, "10 min before"], [15, "15 min before"],
  [30, "30 min before"], [60, "1 hour before"]];
const PAUSE = [["", "Don't switch screens off"], [5, "5 min before"], [10, "10 min before"], [15, "15 min before"],
  [30, "30 min before"]];

function errText(e) {
  return formatApiErrorDetail(e?.response?.data?.detail ?? e?.message);
}

function ago(iso) {
  if (!iso) return "not yet";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return new Date(iso).toLocaleString();
}

/**
 * Family calendar settings: connect Outlook (the master copy), pick the calendar, teach Yoda which events belong
 * to which kid, and set the default reminder / screens-off time.
 */
export default function OutlookCalendarCard({ kids, onChanged }) {
  const [st, setSt] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newRule, setNewRule] = useState({ kind: "keyword", value: "", kid_ids: [] });

  async function load() {
    try {
      const { data } = await api.get("/calendar/outlook");
      setSt(data);
      if (data.connected && !calendars.length) {
        api.get("/calendar/outlook/calendars").then((r) => setCalendars(r.data)).catch(() => {});
      }
    } catch (e) { /* page still works without the card */ }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(fn, ok) {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errText(e));
    } finally { setBusy(false); }
  }

  async function connect() {
    try {
      const { data } = await api.post("/calendar/outlook/start?back=web");
      window.location.href = data.url;
    } catch (e) { toast.error(errText(e)); }
  }

  async function saveSettings(patch, ok) {
    await act(() => api.put("/calendar/settings", patch), ok);
  }

  if (!st) return null;
  const kidName = (id) => (id === "all" ? "Everyone" : kids.find((k) => k.id === id)?.name || "?");
  const chip = (on) => `rounded-full px-3 py-1 text-xs ${on ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`;

  if (!st.connected) {
    return (
      <div className="rounded-2xl bg-white border border-[#F0EFEB] p-5 flex flex-wrap items-center gap-4" data-testid="outlook-card">
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading text-lg">Bring in your Outlook calendar</div>
          <p className="text-sm text-[#707A6E]">Keep using Outlook as you do now. Every event shows up here and each kid
            sees theirs in Yoda Watch. Changes in Outlook come through by themselves.</p>
        </div>
        {st.available ? (
          <button onClick={connect} className="rounded-xl bg-[#2C342A] px-4 py-2 text-sm text-[#F5F4F0]" data-testid="outlook-connect">
            Sign in with Microsoft
          </button>
        ) : (
          <p className="text-xs text-[#C06E52] max-w-[260px]">Not switched on yet: the server needs its Microsoft app
            settings (see docs/AZURE_SETUP.md, "Outlook calendar").</p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-[#F0EFEB] p-5 space-y-5" data-testid="outlook-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading text-lg">Outlook calendar</div>
          <p className="text-sm text-[#707A6E]">
            {st.account} · {st.event_count} events · checked {ago(st.last_sync_at)}
          </p>
          {st.last_error && <p className="text-sm text-[#C06E52]">{st.last_error}</p>}
        </div>
        <select value={st.calendar_id || ""} disabled={busy} data-testid="outlook-calendar"
                onChange={(e) => {
                  const c = calendars.find((x) => x.id === e.target.value);
                  act(() => api.put("/calendar/outlook/calendar", { calendar_id: c.id, calendar_name: c.name }), `Now reading "${c.name}"`);
                }}
                className="rounded-lg border border-[#F0EFEB] px-3 py-2 text-sm bg-white">
          {!calendars.length && <option value={st.calendar_id || ""}>{st.calendar_name || "Calendar"}</option>}
          {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button disabled={busy} onClick={() => act(() => api.post("/calendar/outlook/sync"), "Checked Outlook")}
                className="rounded-lg px-3 py-2 text-sm hover:bg-[#F0EFEB]" data-testid="outlook-sync">Check now</button>
        <button disabled={busy} onClick={() => window.confirm("Stop reading Outlook? Its events leave Yoda Watch (Outlook itself isn't touched).") &&
                act(() => api.delete("/calendar/outlook"), "Outlook disconnected")}
                className="rounded-lg px-3 py-2 text-sm text-[#C06E52] hover:bg-[#F6E3DC]" data-testid="outlook-disconnect">Disconnect</button>
      </div>

      {st.unmatched.length > 0 && (
        <div>
          <div className="text-sm font-medium mb-2">Who are these for? <span className="text-[#707A6E] font-normal">
            Until you pick, only parents see them.</span></div>
          <div className="space-y-2">
            {st.unmatched.map((u) => (
              <div key={u.event_id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="min-w-[180px] flex-1">{u.title}
                  <span className="text-[#707A6E]"> · {new Date(u.start_at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</span></span>
                <button className={chip(false)} onClick={() => act(() => api.patch(`/events/${u.event_id}`, { audience: "everyone", kid_ids: [] }))}>Everyone</button>
                {kids.map((k) => (
                  <button key={k.id} className={chip(false)}
                          onClick={() => act(() => api.patch(`/events/${u.event_id}`, { audience: "kids", kid_ids: [k.id] }))}>{k.name}</button>
                ))}
                <button className={chip(false)} onClick={() => act(() => api.patch(`/events/${u.event_id}`, { audience: "parents", kid_ids: [] }))}>Just us</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-sm font-medium mb-1">How events find the right kid</div>
        <p className="text-xs text-[#707A6E] mb-2">A kid's name in the title always works. Add a colour category or a word
          (like "Soccer") for events that don't say a name.</p>
        <div className="space-y-1">
          {st.rules.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="rounded-md bg-[#F5F4F0] px-2 py-0.5">{r.kind === "category" ? `${r.value} category` : `"${r.value}"`}</span>
              <span className="text-[#707A6E]">→</span>
              <span className="flex-1">{r.kid_ids.map(kidName).join(", ")}</span>
              <button className="text-xs text-[#C06E52]" onClick={() => saveSettings({ rules: st.rules.filter((_, j) => j !== i) })}>Remove</button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <select value={newRule.kind} onChange={(e) => setNewRule({ ...newRule, kind: e.target.value, value: "" })}
                  className="rounded-lg border border-[#F0EFEB] px-2 py-1.5 text-sm bg-white">
            <option value="keyword">Word in the title</option>
            <option value="category">Outlook category</option>
          </select>
          {newRule.kind === "category" && st.categories.length ? (
            <select value={newRule.value} onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                    className="rounded-lg border border-[#F0EFEB] px-2 py-1.5 text-sm bg-white">
              <option value="">Pick a category</option>
              {st.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input value={newRule.value} onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                   placeholder={newRule.kind === "category" ? "Category name" : "e.g. Soccer"}
                   className="rounded-lg border border-[#F0EFEB] px-2 py-1.5 text-sm bg-white w-36" />
          )}
          <button className={chip(newRule.kid_ids.includes("all"))}
                  onClick={() => setNewRule({ ...newRule, kid_ids: newRule.kid_ids.includes("all") ? [] : ["all"] })}>Everyone</button>
          {kids.map((k) => (
            <button key={k.id} className={chip(newRule.kid_ids.includes(k.id))}
                    onClick={() => setNewRule({ ...newRule, kid_ids: newRule.kid_ids.includes(k.id)
                      ? newRule.kid_ids.filter((x) => x !== k.id) : [...newRule.kid_ids.filter((x) => x !== "all"), k.id] })}>{k.name}</button>
          ))}
          <button disabled={busy || !newRule.value.trim() || !newRule.kid_ids.length}
                  onClick={async () => { await saveSettings({ rules: [...st.rules, { ...newRule, value: newRule.value.trim() }] }, "Rule added");
                    setNewRule({ kind: newRule.kind, value: "", kid_ids: [] }); }}
                  className="rounded-lg bg-[#2C342A] px-3 py-1.5 text-xs text-[#F5F4F0] disabled:opacity-40">Add</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <label>
          <div className="text-[#707A6E] mb-1">Remind kids before each event</div>
          <select value={st.default_remind_minutes ?? ""} className="w-full rounded-lg border border-[#F0EFEB] px-2 py-1.5 bg-white"
                  onChange={(e) => saveSettings(e.target.value === "" ? { clear_default_remind: true } : { default_remind_minutes: Number(e.target.value) }, "Saved")}>
            {REMIND.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[#707A6E] mb-1">Screens off before each event</div>
          <select value={st.default_pause_before_minutes ?? ""} className="w-full rounded-lg border border-[#F0EFEB] px-2 py-1.5 bg-white"
                  onChange={(e) => saveSettings(e.target.value === "" ? { clear_default_pause: true } : { default_pause_before_minutes: Number(e.target.value) }, "Saved")}>
            {PAUSE.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 mt-5">
          <input type="checkbox" checked={st.show_private} onChange={(e) => saveSettings({ show_private: e.target.checked }, "Saved")} />
          Kids see events marked Private in Outlook
        </label>
      </div>
    </div>
  );
}
